import { MDBCol, MDBContainer, MDBRow } from "mdbreact";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import images from "../../../assets/img/app";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { PointCardContainer } from "../../../containers/PointCard";
import { Shop } from "../../../containers/Shops";
import { UserContainer } from "../../../containers/User";
import formatDate from "../../../utilities/DateFormatter";
import Accordion from "../../elements/accordion/Accordion";
import CommonButton from "../../elements/common-button/CommonButton";
import ShopIcon from "../../elements/shop-icon/ShopIcon";
import PointIndicator from "../../modules/point-indicator/PointIndicator";
import ShopDescriptionModal from "../../modules/shop-description-modal/ShopDescriptionModal";
import classes from "./PointCardPage.module.scss";
import { handelExternalLinkClick } from "../../../utilities/WespoUtil";
import Description from "../../elements/description/Description";

/**
 * ポイントカード画面コンポーネント
 */
const PointCardPage: React.FC = () => {
  /** 店舗詳細モーダルに表示する店舗 */
  const [selectedShop, setSelectedShop] = useState<Shop | undefined>(undefined);
  /** 店舗詳細モーダルの表示状態 */
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const history = useHistory();
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const pointCardContainer = PointCardContainer.useContainer();

  const user = userContainer.user;
  const userId = user.id || user.rawId;

  const campaign = campaignContainer.campaign;
  const campaignId = campaignContainer.campaignId;

  const isReached = campaignContainer.isReached;

  const pointCard = pointCardContainer.pointCard;
  const currentCharge = pointCard?.charge || 0;
  const isCompleted = (campaign?.setting.targetAmount || 0) <= currentCharge;

  const format = "YYYY年MM月DD日 (AA)";
  const distributionDate = formatDate(
    format,
    campaign?.outline.incentive.distributionDate
  );
  const expirationDate = formatDate(
    format,
    campaign?.outline.incentive.expirationDate
  );

  if (!appContainer.context.loading) {
    if (isReached === undefined) {
      // キャンペーン達成者数を確認
      appContainer.setLoading(true);
      campaignContainer
        .checkAchievedParticipants(campaignId)
        .then(() => {
          appContainer.setLoading(false);
        })
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }
  }

  useEffect(() => {
    // ユーザーが特定できない場合はエラー画面に遷移
    if (!user.id) {
      errorContainer.setCustomError(
        "ポイントカード未登録",
        [
          "アカウントを特定できませんでした。",
          "キャンペーン対象施設のポイントカードを",
          "登録・新規発行してから",
          "再度アクセスしてください。",
        ],
        userId
      );
      return;
    }

    // キャンペーン上限に達した場合はエラー画面に遷移
    if (
      (!pointCard?.isEntry || !isCompleted) &&
      (isReached || pointCard?.updateResult === "SKIP")
    ) {
      errorContainer.setCustomError(
        "上限に達しました",
        [
          "ご好評につき、当キャンペーンは",
          "上限に達したため",
          "終了いたしました。",
          "次回キャンペーンへのご参加を",
          "お待ちしております。",
        ],
        userId,
        false
      );
      return;
    }

    // 未エントリーの場合はエントリー処理を実行
    if (
      !appContainer.context.loading &&
      campaign &&
      pointCard &&
      !pointCard.isEntry
    ) {
      const campaignStartDate = formatDate(
        format,
        campaign?.outline.period.from
      );
      appContainer.setLoading(true);
      pointCardContainer
        .entry(
          campaignId,
          campaign.outline.period.from,
          campaign.outline.period.to
        )
        .then((result) => {
          if (result === "preparing") {
            errorContainer.setCustomError(
              "キャンペーン期間外",
              [
                `本キャンペーンは ${campaignStartDate}`,
                "より開始します。",
                "キャンペーンの開始までお待ちください。",
              ],
              userId
            );
          } else if (result === "end") {
            errorContainer.setCustomError(
              "キャンペーン期間外",
              [
                "本キャンペーンは終了しました。",
                "次回のキャンペーンをお待ちください。",
              ],
              userId
            );
          }
          appContainer.setLoading(false);
        })
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }

    // エントリー済みであればポイントカードを更新
    if (
      pointCard?.isEntry &&
      !pointCard?.isLatest &&
      user.id &&
      user.hashedId
    ) {
      // ポイントカードの更新
      appContainer.setLoading(true);
      const defaultRecommends = campaign?.setting.defaultRecommends.map(
        (dr: any): string => dr.id
      );
      pointCardContainer
        .initPointCard(
          user.id,
          user.hashedId,
          campaignId,
          defaultRecommends || [],
          user.token || ""
        )
        .then(() => appContainer.setLoading(false))
        .catch((error) => errorContainer.setDefaultError(userId, error));
    }
  }, [
    appContainer,
    campaign,
    campaignId,
    errorContainer,
    isCompleted,
    isReached,
    pointCard,
    pointCardContainer,
    user.hashedId,
    user.id,
    user.token,
    userId,
  ]);

  /**
   * 対象店舗一覧ボタンクリック時の処理
   */
  const handleShopListButtonClick = () => {
    if (campaign?.outline.targetShopsLink?.url) {
      handelExternalLinkClick(null, campaign.outline.targetShopsLink.url);
      return;
    }

    history.push(`/${campaignId}/shop-list/${user.rawId}`);
  };

  /**
   * TOP に戻るボタンクリック時の処理
   */
  const handleReturnButtonClick = () => {
    history.push(`/${campaignId}/${user.rawId}`);
  };

  return (
    <React.Fragment>
      <MDBContainer fluid>
        <div className="py-3">
          <div className={classes.content}>
            <div className={classes.contentTitle}>
              {campaign?.outline.title}
            </div>

            <div className={classes.statusIndicator}>
              <div className={classes.title}>《 あなたのステータス 》</div>
              <div className={classes.body}>
                <div className={classes.indicator}>
                  <PointIndicator
                    total={campaign?.setting.targetAmount}
                    current={pointCard?.charge}
                    incentive={campaign?.setting.incentive.point}
                  ></PointIndicator>
                </div>
              </div>
              <div className={classes.notice}>
                <Description
                  message={campaign?.notices.pointCard}
                ></Description>
              </div>
            </div>

            {campaign?.status === "end" ? (
              <div className={classes.inactiveMessage}>
                {campaign.notices.finishedDescription
                  ? campaign.notices.finishedDescription.map((v, i) => {
                      return <div key={i}>{v}</div>;
                    })
                  : "本キャンペーンは終了しました。以降、対象店舗をご利用いただいた場合でも、購買金額は加算されませんのでご注意ください。"}
              </div>
            ) : null}
          </div>

          {isCompleted ? (
            <React.Fragment>
              <div className={classes.completeMessage}>
                <div className={classes.title}>
                  <img
                    className={classes.congratsImg}
                    src={images.congrats}
                    alt=""
                  ></img>
                </div>
                <div className={`${classes.message} px-3`}>
                  <Description
                    message={campaign?.messages.complete}
                  ></Description>
                </div>
                {/* <div className={`${classes.schedule} px-3`}>
                  <div className="mb-1">
                    <span className={classes.name}>ポイント付与予定：</span>
                    {distributionDate}
                  </div>
                  <div>
                    <span className={classes.name}>ポイント有効期限：</span>
                    {expirationDate}
                  </div>
                </div> */}
                {campaign?.notices.incentive ? (
                  <div className="px-3">
                    <Accordion
                      title={"注意事項（必ずお読みください）"}
                      contents={campaign.notices.incentive}
                      bgColor={"lightyellow"}
                    ></Accordion>
                  </div>
                ) : null}
              </div>
            </React.Fragment>
          ) : (pointCard?.recommends?.length || 0) > 0 ? (
            <div className={classes.content}>
              <div className={classes.contentTitle}>おすすめ店舗</div>

              <div className={classes.shopList}>
                <MDBRow around className="mx-0">
                  {pointCard?.recommends?.map((shop: Shop, idx) => {
                    return (
                      <MDBCol key={idx} size="3" className="px-1">
                        <ShopIcon
                          image={shop.image}
                          name={shop.name}
                          onClick={() => {
                            setSelectedShop(shop);
                            setIsModalOpen(true);
                          }}
                        ></ShopIcon>
                      </MDBCol>
                    );
                  })}
                </MDBRow>
              </div>
            </div>
          ) : null}

          <div>
            <div className="px-3">
              {!isCompleted ? (
                <div className="pb-3">
                  <CommonButton
                    fullWidth
                    outline
                    onClick={handleShopListButtonClick}
                  >
                    {campaign?.outline.targetShopsLink?.label ||
                      "対象店舗はこちら"}
                  </CommonButton>
                </div>
              ) : null}
              <div>
                <CommonButton
                  fullWidth
                  outline
                  onClick={handleReturnButtonClick}
                >
                  TOPに戻る
                </CommonButton>
              </div>
            </div>
          </div>
        </div>
      </MDBContainer>

      <ShopDescriptionModal
        isOpen={isModalOpen}
        shop={selectedShop}
        notices={campaign?.notices.shopDescription}
        onCloseButtonClick={() => {
          setIsModalOpen(false);
        }}
      ></ShopDescriptionModal>
    </React.Fragment>
  );
};

export default PointCardPage;
